import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCivilLitigationWizardContext } from '../../../hooks/useCaseRequestWizard';
import FormattedDateInput from '../../../components/ui/FormattedDateInput';
import FormattedDateRangeInput from '../../../components/ui/FormattedDateRangeInput';
import { exceedsMax, isTodayOrFutureIsoDate, maxCharLimitExceededError } from '../../../utils/caseFieldValidation';
import { validationDateFutureOnly } from '../../../utils/validationI18n';
import { useWizardMaxLengthChange } from '../useWizardMaxLengthChange';

import { focusFirstInvalid, type RequiredErrors } from '../../../utils/requiredValidation';
import { SectionCard } from '../WizardComponents';
import WizardSelect from '../../../components/wizard/WizardSelect';
import { CivilLitigationWizardActions } from './CivilLitigationWizardActions';
import { civilLitigationWizardPath, CivilLitigationWizardShell, FieldError, inpClass } from './CivilLitigationWizardShell';
import { useCivilLitigationLookups } from './useCivilLitigationLookups';
import { isHighUrgencyLevel, isOtherNextStep } from './civilLitigationHelpers';
import { minDeadlineDateForCivilLitigation, validateCivilLitigationStep } from './civilLitigationStepValidation';
import { useWizardFieldLabels } from '../useWizardFieldLabels';

const MAX_TEXT = 2000;
const MAX_REF = 255;

export default function CivilLitigationStep3IncidentDetailsPage() {
  const f = useWizardFieldLabels();
  const nav = useNavigate();
  const { draft, setDraft } = useCivilLitigationWizardContext();
  const [errors, setErrors] = useState<RequiredErrors>({});
  const { urgencyLevels, nextSteps } = useCivilLitigationLookups('step3');
  const minDeadlineDate = useMemo(() => minDeadlineDateForCivilLitigation(), []);
  const urgencyJustificationRequired = isHighUrgencyLevel(draft.urgencyLevelName);
  const showNextStepOther = isOtherNextStep(draft.nextStepOfCaseName, draft.nextStepOfCaseId);

  const clearErr = (key: string) => setErrors((e) => ({ ...e, [key]: '' }));
  const maxLen = useWizardMaxLengthChange(setErrors);

  const validate = (): boolean => {
    const { valid, errors: nextErrors, specs } = validateCivilLitigationStep(3, draft);
    setErrors(nextErrors);
    if (!valid) focusFirstInvalid(specs, nextErrors);
    return valid;
  };

  return (
    <CivilLitigationWizardShell currentStep={3} draft={draft}>
      <SectionCard title={f.sectionIncidentMatterDetails}>
        <div className="ccr-fg" style={{ marginBottom: 14 }}>
          <label>
            {f.subjectOfDispute} <span className="req">*</span>
          </label>
          <textarea
            id="clit-subject"
            className={inpClass(Boolean(errors.subjectOfDispute))}
            rows={4}
            value={draft.subjectOfDispute}
            onChange={(e) => {
              setDraft((d) => ({ ...d, subjectOfDispute: maxLen('subjectOfDispute', e.target.value, MAX_TEXT) }));
            }}
            placeholder={f.detailedLegalIssueDescription}
          />
          <FieldError message={errors.subjectOfDispute} />
        </div>

        <div className="ccr-fg ccr-field-half" style={{ marginBottom: 14 }}>
          <label>{f.incidentOccurredDate}</label>
          <FormattedDateRangeInput
            from={draft.incidentDateFrom}
            to={draft.incidentDateTo}
            enableYmdViews
            onChange={(from, to) => setDraft((d) => ({ ...d, incidentDateFrom: from, incidentDateTo: to }))}
          />
        </div>

        <div className="ccr-fr c2" style={{ marginBottom: showNextStepOther ? 14 : 0 }}>
          <div className="ccr-fg">
            <label>{f.nextStepOfCaseIfApplicable}</label>
            <WizardSelect
              value={draft.nextStepOfCaseId}
              placeholder={f.selectNextStep}
              options={nextSteps.map((s) => ({ id: s.id, label: s.name }))}
              onChange={(id) => {
                const row = nextSteps.find((s) => s.id === id);
                const name = row?.name ?? '';
                const otherSelected = isOtherNextStep(name, row?.code ?? id);
                setDraft((d) => ({
                  ...d,
                  nextStepOfCaseId: id,
                  nextStepOfCaseName: name,
                  nextStepOfCaseOther: otherSelected ? d.nextStepOfCaseOther : '',
                }));
                if (!otherSelected) clearErr('nextStepOfCaseOther');
              }}
            />
          </div>
          <div className="ccr-fg">
            <label>{f.nextDateOfCase}</label>
            <FormattedDateInput
              id="clit-nextCaseDate"
              className={inpClass(Boolean(errors.nextCaseDate))}
              value={draft.nextCaseDate}
              minDate={minDeadlineDate}
              enableYmdViews
              onChange={(next) => {
                setDraft((d) => ({ ...d, nextCaseDate: next }));
                clearErr('nextCaseDate');
                if (next && !isTodayOrFutureIsoDate(next)) {
                  setErrors((e) => ({ ...e, nextCaseDate: validationDateFutureOnly() }));
                }
              }}
            />
            <FieldError message={errors.nextCaseDate} />
          </div>
        </div>

        {showNextStepOther ? (
          <div className="ccr-fg" style={{ marginBottom: 0 }}>
            <label>{f.nextStepOther}</label>
            <input
              id="clit-nextStepOther"
              className={inpClass(Boolean(errors.nextStepOfCaseOther))}
              type="text"
              value={draft.nextStepOfCaseOther}
              onChange={(e) => {
                setDraft((d) => ({ ...d, nextStepOfCaseOther: maxLen('nextStepOfCaseOther', e.target.value, MAX_REF) }));
                clearErr('nextStepOfCaseOther');
              }}
              placeholder={f.enterNextStepOther}
            />
            <FieldError message={errors.nextStepOfCaseOther} />
          </div>
        ) : null}
      </SectionCard>

      <SectionCard title={f.sectionTimeline}>
        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>
              {f.urgencyLevel} <span className="req">*</span>
            </label>
            <WizardSelect
              id="clit-urgency"
              hasError={Boolean(errors.urgencyLevelId)}
              value={draft.urgencyLevelId}
              placeholder={f.selectUrgencyLevel}
              options={urgencyLevels.map((u) => ({ id: u.id, label: u.name }))}
              onChange={(id) => {
                const name = urgencyLevels.find((u) => u.id === id)?.name ?? '';
                setDraft((d) => ({
                  ...d,
                  urgencyLevelId: id,
                  urgencyLevelName: name }));
                if (!isHighUrgencyLevel(name)) clearErr('urgencyJustification');
                clearErr('urgencyLevelId');
              }}
            />
            <FieldError message={errors.urgencyLevelId} />
          </div>
          <div className="ccr-fg">
            <label>{f.deadlineIfAny}</label>
            <FormattedDateInput
              id="clit-deadline"
              className={inpClass(Boolean(errors.deadline))}
              value={draft.deadline}
              minDate={minDeadlineDate}
              enableYmdViews
              onChange={(next) => {
                setDraft((d) => ({ ...d, deadline: next }));
                clearErr('deadline');
                if (next && !isTodayOrFutureIsoDate(next)) {
                  setErrors((e) => ({ ...e, deadline: validationDateFutureOnly() }));
                }
              }}
            />
            <FieldError message={errors.deadline} />
          </div>
        </div>
        <div className="ccr-fg" style={{ marginBottom: 0 }}>
          <label>
            {f.urgencyJustification} {urgencyJustificationRequired ? <span className="req">*</span> : null}
          </label>
          <textarea
            id="clit-urgencyJustification"
            className={inpClass(Boolean(errors.urgencyJustification))}
            rows={3}
            value={draft.urgencyJustification}
            onChange={(e) => {
              setDraft((d) => ({ ...d, urgencyJustification: maxLen('urgencyJustification', e.target.value, MAX_TEXT) }));
            }}
            placeholder={f.urgencyJustificationReason}
          />
          <FieldError message={errors.urgencyJustification} />
        </div>
      </SectionCard>

      <CivilLitigationWizardActions
        draft={draft}
        setDraft={setDraft}
        currentStep={3}
        onBack={() => nav(civilLitigationWizardPath(2, draft.draftRequestId))}
        onNext={() => {
          if (validate()) nav(civilLitigationWizardPath(4, draft.draftRequestId));
        }}
        validateBeforeSave={validate}
      />
    </CivilLitigationWizardShell>
  );
}
